import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { axios } from '../../libs/axios';
import { useTranslation } from '../../i18n/I18nContext';
import { ComponentStackedBar, QvsLScatter, HistogramBar, FieldDemandSupplyScatter, fmtNum, fmtPct, fmtScore, fmtInt } from './charts';
import { LawyerResultsTable } from './LawyerResultsTable';

function SectionTakeaway({ children, variant = 'muted' }) {
  if (!children) return null;
  return <p className={`match-overview-takeaway match-overview-takeaway--${variant}`}>{children}</p>;
}

function paramMismatch(computed, param, threshold = 0.2) {
  if (computed == null || param == null || Number.isNaN(Number(computed)) || Number.isNaN(Number(param))) {
    return false;
  }
  const denom = Math.max(Math.abs(Number(param)), 0.001);
  return Math.abs(Number(computed) - Number(param)) / denom > threshold;
}

function formatDt(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

function ExcludeModal({ open, project, onClose, onSubmit, t }) {
  const [scope, setScope] = useState('both');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setScope('both');
      setReason('');
      setError(null);
    }
  }, [open, project?.project_no]);

  if (!open || !project) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t('match.exclusion.reasonRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ project_no: project.project_no, scope, reason: reason.trim() });
      onClose();
    } catch (err) {
      setError(err?.message || t('match.exclusion.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="match-exclude-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="match-exclude-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
        <h5>{t('match.exclusion.title', { project: project.project_no })}</h5>
        <form onSubmit={handleSubmit}>
          <label className="d-block small">
            {t('match.exclusion.scope')}
            <select className="form-control form-control-sm mt-1" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="speed">{t('match.exclusion.scopeSpeed')}</option>
              <option value="sla">{t('match.exclusion.scopeSla')}</option>
              <option value="both">{t('match.exclusion.scopeBoth')}</option>
            </select>
          </label>
          <label className="d-block small mt-2">
            {t('match.exclusion.reason')}
            <textarea
              className="form-control form-control-sm mt-1"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </label>
          {error && <p className="text-danger small mt-2">{error}</p>}
          <p className="text-muted small mt-2">{t('match.exclusion.recomputeNote')}</p>
          <div className="mt-3">
            <button type="submit" className="btn btn-sm btn-primary mr-2" disabled={saving}>
              {t('match.exclusion.confirm')}
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectMetricsTable({ rows, onExclude, t }) {
  if (!rows?.length) return <p className="text-muted small">{t('match.overview.noProjects')}</p>;
  return (
    <div className="match-table-scroll">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            <th>{t('match.projectNo')}</th>
            <th>{t('match.colName')}</th>
            <th>{t('match.overview.assignment')}</th>
            <th>{t('match.overview.firstResponse')}</th>
            <th>{t('match.col.bizHours')}</th>
            <th>{t('match.overview.disposition')}</th>
            <th>{t('match.col.deadline')}</th>
            <th>{t('match.col.overage')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.project_no}>
              <td>
                <Link to={`/projects/${p.project_no}`}>{p.project_no}</Link>
              </td>
              <td>{p.lawyerName}</td>
              <td className="small">{formatDt(p.assignmentAt)}</td>
              <td className="small">{formatDt(p.firstResponseAt)}</td>
              <td>{p.elapsedHours != null ? fmtScore(p.elapsedHours) : '—'}</td>
              <td className="small">{p.speedReason || p.speedDisposition}{p.slaDisposition === 'breach' ? ' / breach' : ''}</td>
              <td className="small">{formatDt(p.deadlineBoundary || p.deadlineMoment)}</td>
              <td>{p.overageHours != null ? fmtScore(p.overageHours) : '—'}</td>
              <td>
                {p.speedReason !== 'manually_excluded' && p.slaReason !== 'manually_excluded' && (
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onExclude(p)}>
                    {t('match.exclusion.exclude')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BaselinesPanel({ overview, sDistribution, t }) {
  const baselines = overview?.platformBaselines;
  const paramsInUse = overview?.paramsInUse || {};
  if (!baselines && !paramsInUse.t0) return null;

  const rows = [
    { label: t('match.baselines.platformMedianResponse'), computed: baselines?.platformMedianResponseH, param: paramsInUse.t0, fmt: (v) => (v != null ? fmtNum(v, 1) : '—') },
    { label: t('match.baselines.platformMeanRating'), computed: baselines?.platformMeanRating, param: paramsInUse.r0, fmt: (v) => (v != null ? fmtNum(v, 1) : '—') },
    { label: t('match.baselines.medianK'), computed: baselines?.medianK, param: null, fmt: (v) => (v != null ? fmtInt(v) : '—') },
    { label: t('match.baselines.noSpeed'), computed: baselines?.noSpeedCount, param: null, fmt: (v) => (v != null ? fmtInt(v) : '—') },
    { label: t('match.baselines.noRating'), computed: baselines?.noRatingCount, param: null, fmt: (v) => (v != null ? fmtInt(v) : '—') },
  ];
  const mismatchCount = rows.filter((r) => r.param != null && paramMismatch(r.computed, r.param)).length;

  return (
    <section className="match-baselines-panel mb-4">
      <h4>{t('match.baselines.title')}</h4>
      {baselines?.computedAt && (
        <p className="text-muted small">{t('match.baselines.computedAt')}: {new Date(baselines.computedAt).toLocaleString()}</p>
      )}
      <table className="table table-sm match-baselines-table">
        <thead>
          <tr><th /><th>{t('match.baselines.computed')}</th><th>{t('match.baselines.paramInUse')}</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const mismatch = row.param != null && paramMismatch(row.computed, row.param);
            return (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.fmt(row.computed)}</td>
                <td>{row.param != null ? row.fmt(row.param) : '—'}</td>
                <td>
                  {row.param != null && (
                    mismatch
                      ? <span className="match-baselines-mismatch">⚠ {t('match.baselines.mismatch')}</span>
                      : <span className="match-baselines-ok">{t('match.baselines.ok')}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sDistribution?.length > 0 && (
        <div className="match-s-distribution">
          <h6>{t('match.baselines.sDistribution', { tau: paramsInUse.tau ?? '—' })}</h6>
          <p className="small text-muted match-s-distribution-values">{sDistribution.map((s) => fmtScore(s)).join(', ')}</p>
        </div>
      )}
      {!baselines?.computedAt && <p className="text-muted small">{t('match.baselines.notYetComputed')}</p>}
      <SectionTakeaway>
        {t('match.overview.baselinesTakeaway', {
          mismatch: mismatchCount,
          noSpeed: baselines?.noSpeedCount ?? 0,
          noRating: baselines?.noRatingCount ?? 0,
        })}
      </SectionTakeaway>
    </section>
  );
}

export function OverviewTab({ paramsProfile }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [lawyers, setLawyers] = useState([]);
  const [calibration, setCalibration] = useState(null);
  const [nonDiscriminating, setNonDiscriminating] = useState([]);
  const [sDistribution, setSDistribution] = useState([]);
  const [fieldWindow, setFieldWindow] = useState('90d');
  const [fieldSupplyMode, setFieldSupplyMode] = useState('expertise');
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [bucketProjects, setBucketProjects] = useState([]);
  const [slaBreaches, setSlaBreaches] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [loadingBucket, setLoadingBucket] = useState(false);

  const reloadOverview = useCallback(async () => {
    const [ov, lw, ex] = await Promise.all([
      axios.get('/match/overview', { params: { paramsProfile } }),
      axios.get('/match/lawyers', { params: { paramsProfile } }),
      axios.get('/match/metric-exclusions'),
    ]);
    setOverview(ov);
    setLawyers(lw.rows || []);
    setCalibration(lw.calibration || null);
    setNonDiscriminating(lw.nonDiscriminating || []);
    setSDistribution(lw.sDistribution || []);
    setExclusions(ex.exclusions || ov.activeExclusions || []);
    return ov;
  }, [paramsProfile]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ov = await reloadOverview();
        const breachRes = await axios.get('/match/overview/sla-breaches', { params: { paramsProfile } });
        setSlaBreaches(breachRes.breaches || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [paramsProfile, reloadOverview]);

  const stackedData = useMemo(() => lawyers.slice(0, 15).map((r) => ({
    name: (r.name || r.user_no).slice(0, 12),
    fullName: r.name || r.user_no,
    sigma: r.sigma,
    wE_E: r.wE_E,
    wR_R: r.wR_R,
    wS_S: r.wS_S,
    loss: Math.max(0, r.Q - r.sigma),
  })), [lawyers]);

  const scatterData = useMemo(() => lawyers.map((r) => ({ L: r.L, Q: r.Q, name: r.name || r.user_no })), [lawyers]);
  const theta = overview?.paramsInUse?.theta ?? calibration?.theta;

  const histData = useMemo(() => {
    const buckets = overview?.responseHistogram || {};
    return Object.entries(buckets).map(([bucket, count]) => ({ bucket, count }));
  }, [overview]);

  const extraColumns = [
    { key: 'exclusionCount', label: t('match.col.exclusionCount'), fmtType: 'int' },
    { key: 'E', label: t('match.col.E'), fmtType: 'score' },
    { key: 'R', label: t('match.col.R'), fmtType: 'score' },
    { key: 'S', label: t('match.col.S'), fmtType: 'score' },
    {
      key: 'respMedianH',
      label: t('match.col.respMedianH'),
      fmt: (v, row) => ((row.k ?? 0) > 0 && v != null ? `${fmtNum(v, 1)} h` : '—'),
      title: (row) => {
        const unit = overview?.paramsInUse?.toggleBusinessHoursSpeed !== false
          ? t('match.respMedianH.unitBusiness')
          : t('match.respMedianH.unitCalendar');
        return t('match.colTooltip.respMedianH', {
          unit,
          tStar: row.tStar != null ? fmtNum(row.tStar, 1) : '—',
          k: row.k ?? 0,
        });
      },
    },
    { key: 'Q', label: t('match.col.Q'), fmtType: 'score' },
    { key: 'A', label: t('match.col.A'), fmtType: 'score' },
    { key: 'v', label: t('match.col.v'), fmtType: 'score' },
    { key: 'D', label: t('match.col.D'), fmtType: 'score' },
    { key: 'sigma', label: t('match.col.sigma'), fmtType: 'score' },
    { key: 'n', label: t('match.col.n'), fmtType: 'int' },
    { key: 'k', label: t('match.col.k'), fmtType: 'int' },
  ];

  const crossTabRows = useMemo(() => {
    if (!overview?.crossTab?.filtered) return [];
    return Object.entries(overview.crossTab.filtered).map(([key, count]) => {
      const [project_type, status, hasTask, hasMsg, age] = key.split('|');
      return { key, project_type, status, hasTask, hasMsg, age, count };
    }).sort((a, b) => b.count - a.count);
  }, [overview]);

  const fieldUsageRows = useMemo(() => {
    const rows = overview?.fieldUsage?.rows || [];
    if (fieldWindow === 'all') {
      return [...rows].sort((a, b) => b.countAll - a.countAll);
    }
    return rows;
  }, [overview, fieldWindow]);

  const handleBucketClick = async (bucket) => {
    if (!bucket) return;
    setSelectedBucket(bucket);
    setLoadingBucket(true);
    try {
      const res = await axios.get('/match/overview/response-bucket', { params: { paramsProfile, bucket } });
      setBucketProjects(res.projects || []);
    } finally {
      setLoadingBucket(false);
    }
  };

  const handleExclude = async ({ project_no, scope, reason }) => {
    await axios.post('/match/metric-exclusions', { project_no, scope, reason });
    await reloadOverview();
    if (selectedBucket) await handleBucketClick(selectedBucket);
    const breachRes = await axios.get('/match/overview/sla-breaches', { params: { paramsProfile } });
    setSlaBreaches(breachRes.breaches || []);
  };

  const handleRestoreExclusion = async (id) => {
    await axios.delete(`/match/metric-exclusions/${id}`);
    await reloadOverview();
    if (selectedBucket) await handleBucketClick(selectedBucket);
    const breachRes = await axios.get('/match/overview/sla-breaches', { params: { paramsProfile } });
    setSlaBreaches(breachRes.breaches || []);
  };

  const integrity = overview?.timestampIntegrity;
  const jaccardSummary = overview?.domainCoverage?.jaccardSummary;
  const highJaccardPairs = jaccardSummary?.allPairs?.filter((p) => p.jaccard > 0.8) || overview?.domainCoverage?.jaccardPairs || [];

  if (loading) return <div>{t('common.loading')}</div>;

  return (
    <div className="match-overview">
      <BaselinesPanel overview={overview} sDistribution={sDistribution} t={t} />

      {nonDiscriminating.length > 0 && (
        <div className="alert alert-warning">
          {t('match.nonDiscriminating')}: {nonDiscriminating.join(', ')}
        </div>
      )}

      {calibration && (
        <div className="mb-4 match-overview-section">
          <h4>{t('match.capacityCalibration')}</h4>
          <p className="text-muted small">{t('match.overview.capacityPurpose')}</p>
          <p className={calibration.capsSensible ? 'text-success' : 'text-warning'}>{calibration.capsMessage}</p>
          <p className="small text-muted">
            {t('match.overview.capacityCaps', {
              weekly: calibration.weeklyCap ?? overview?.paramsInUse?.weeklyIntakeCap,
              backlog: calibration.backlogCap ?? overview?.paramsInUse?.backlogCap,
              penalized: calibration.penalizedCount ?? 0,
              excluded: calibration.excludedCount ?? 0,
            })}
          </p>
          <div className="row mb-2">
            <div className="col-md-4"><h6>N7</h6><p className="small text-muted">{(calibration.distributions?.N7 || []).join(', ')}</p></div>
            <div className="col-md-4"><h6>N14</h6><p className="small text-muted">{(calibration.distributions?.N14 || []).join(', ')}</p></div>
            <div className="col-md-4"><h6>Uq</h6><p className="small text-muted">{(calibration.distributions?.Uq || []).join(', ')}</p></div>
          </div>
          <table className="table table-sm">
            <thead><tr><th>{t('match.colName')}</th><th>N7</th><th>N14</th><th>flow</th><th>Uq</th><th>L</th><th>A</th></tr></thead>
            <tbody>
              {(calibration.lawyers || []).map((r) => (
                <tr key={r.user_no}>
                  <td>{r.name}</td>
                  <td>{fmtInt(r.N7)}</td><td>{fmtInt(r.N14)}</td><td>{fmtScore(r.flow)}</td>
                  <td>{fmtInt(r.Uq)}</td><td>{fmtScore(r.L)}</td><td>{fmtScore(r.A)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <SectionTakeaway variant={calibration.capsSensible ? 'success' : 'warning'}>
            {t('match.overview.capacityTakeaway', {
              medianL: fmtNum(calibration.medianL),
              penalized: calibration.penalizedCount ?? 0,
              excluded: calibration.excludedCount ?? 0,
            })}
          </SectionTakeaway>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-6 match-overview-section">
          <h4>{t('match.stackedBar')}</h4>
          <ComponentStackedBar
            data={stackedData}
            labels={{
              expertise: t('match.overview.seriesExpertise'),
              rating: t('match.overview.seriesRating'),
              speed: t('match.overview.seriesSpeed'),
              loss: t('match.overview.seriesLoss'),
            }}
          />
          <SectionTakeaway>{t('match.overview.stackedTakeaway')}</SectionTakeaway>
        </div>
        <div className="col-md-6 match-overview-section">
          <h4>{t('match.qVsL')}</h4>
          <QvsLScatter
            data={scatterData}
            theta={theta}
            axisLabels={{ load: t('match.overview.axisLoad'), quality: t('match.overview.axisQuality') }}
          />
          <SectionTakeaway>{t('match.overview.scatterTakeaway', { theta: fmtNum(theta) })}</SectionTakeaway>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6 match-overview-section">
          <HistogramBar
            data={histData}
            title={t('match.responseHist')}
            onBucketClick={handleBucketClick}
            selectedBucket={selectedBucket}
          />
          <SectionTakeaway>{t('match.overview.histogramTakeaway')}</SectionTakeaway>
          {selectedBucket && (
            <div className="mt-3">
              <h5>{t('match.overview.bucketProjects', { bucket: selectedBucket })}</h5>
              {loadingBucket ? <p>{t('common.loading')}</p> : (
                <ProjectMetricsTable rows={bucketProjects} onExclude={setExcludeTarget} t={t} />
              )}
            </div>
          )}
        </div>
        <div className="col-md-6 match-overview-section">
          <h4>{t('match.assignmentDist')}</h4>
          {overview?.assignmentDistribution && (
            <ul>
              <li>Gini: {fmtNum(overview.assignmentDistribution.gini)}</li>
              <li>{t('match.top3Share')}: {fmtPct(overview.assignmentDistribution.top3Share)}</li>
              <li>{t('match.top5Share')}: {fmtPct(overview.assignmentDistribution.top5Share)}</li>
              <li>{t('match.total90d')}: {overview.assignmentDistribution.total}</li>
            </ul>
          )}
          <SectionTakeaway>{t('match.overview.assignmentTakeaway')}</SectionTakeaway>
        </div>
      </div>

      <section className="mb-4 match-overview-section">
        <h4>{t('match.overview.slaBreaches')}</h4>
        <ProjectMetricsTable rows={slaBreaches} onExclude={setExcludeTarget} t={t} />
        <SectionTakeaway>{t('match.overview.slaTakeaway', { count: slaBreaches.length })}</SectionTakeaway>
      </section>

      <section className="mb-4 match-overview-section">
        <h4>{t('match.exclusion.listTitle')}</h4>
        <p className="text-muted small">{t('match.exclusion.recomputeNote')}</p>
        {exclusions.length === 0 ? (
          <p className="text-muted small">{t('match.exclusion.none')}</p>
        ) : (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>{t('match.projectNo')}</th>
                <th>{t('match.exclusion.scope')}</th>
                <th>{t('match.exclusion.reason')}</th>
                <th>{t('match.exclusion.excludedAt')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {exclusions.map((ex) => (
                <tr key={ex.id}>
                  <td><Link to={`/projects/${ex.project_no}`}>{ex.project_no}</Link></td>
                  <td>{ex.scope}</td>
                  <td>{ex.reason}</td>
                  <td className="small">{formatDt(ex.excludedAt)}</td>
                  <td>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => handleRestoreExclusion(ex.id)}>
                      {t('match.exclusion.restore')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {overview?.typesWithResponses?.flags?.length > 0 && (
        <div className="alert alert-info">
          <h5>{t('match.typesWithResponses')}</h5>
          <ul>
            {overview.typesWithResponses.flags.map((f) => (
              <li key={f.project_type}>
                {f.project_type}: {f.withResponse} responses (samples: {f.sampleHours.join(', ')}h)
              </li>
            ))}
          </ul>
        </div>
      )}

      <section className="match-overview-section mb-4">
        <h4>{t('match.lawyerTable')}</h4>
        <p className="text-muted small">{t('match.informationalNote')}</p>
        <LawyerResultsTable rows={lawyers} extraColumns={extraColumns} paramsProfile={paramsProfile} />
        <SectionTakeaway>{t('match.overview.lawyerTableTakeaway')}</SectionTakeaway>
      </section>

      <section className="match-overview-section mb-4">
        <h4>{t('match.domainCoverage')}</h4>
        {overview?.domainCoverage?.coverage && (
          <table className="table table-sm">
            <thead><tr><th>field_id</th><th>{t('match.name')}</th><th>{t('match.lawyers')}</th><th>{t('match.overview.expertiseCoverage')}</th><th>{t('match.flag')}</th></tr></thead>
            <tbody>
              {overview.domainCoverage.coverage.map((c) => (
                <tr key={c.field_id} className={c.flag ? `flag-${c.flag}` : ''}>
                  <td>{c.field_id}</td><td>{c.name_et}</td><td>{c.lawyerCount}</td><td>{c.expertiseCount ?? 0}</td><td>{c.flag || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <SectionTakeaway>{t('match.overview.domainCoverageTakeaway')}</SectionTakeaway>
      </section>

      <section className="match-overview-section mb-4">
        <h4>{t('match.overview.fieldUsage')}</h4>
        <p className="text-muted small">{t('match.overview.fieldUsageNote')}</p>
        <div className="d-flex flex-wrap gap-2 mb-2">
          <div className="btn-group btn-group-sm">
            <button type="button" className={`btn btn-outline-secondary${fieldWindow === '90d' ? ' active' : ''}`} onClick={() => setFieldWindow('90d')}>
              {t('match.overview.fieldWindow90d')}
            </button>
            <button type="button" className={`btn btn-outline-secondary${fieldWindow === 'all' ? ' active' : ''}`} onClick={() => setFieldWindow('all')}>
              {t('match.overview.fieldWindowAll')}
            </button>
          </div>
          <div className="btn-group btn-group-sm">
            <button type="button" className={`btn btn-outline-secondary${fieldSupplyMode === 'lawyers' ? ' active' : ''}`} onClick={() => setFieldSupplyMode('lawyers')}>
              {t('match.overview.supplyModeLawyers')}
            </button>
            <button type="button" className={`btn btn-outline-secondary${fieldSupplyMode === 'expertise' ? ' active' : ''}`} onClick={() => setFieldSupplyMode('expertise')}>
              {t('match.overview.supplyModeExpertise')}
            </button>
          </div>
        </div>
        <FieldDemandSupplyScatter
          data={fieldUsageRows}
          supplyMode={fieldSupplyMode}
          fieldWindow={fieldWindow}
          axisLabels={{
            demand: fieldWindow === '90d'
              ? t('match.overview.projects90d')
              : t('match.overview.projectsAll'),
            supplyLawyers: t('match.lawyers'),
            supplyExpertise: t('match.overview.expertiseCoverage'),
          }}
          tooltipLabels={{
            projects: fieldWindow === '90d'
              ? t('match.overview.projects90d')
              : t('match.overview.projectsAll'),
            lawyers: t('match.lawyers'),
            expertise: t('match.overview.expertiseCoverage'),
            ratio: t('match.overview.demandSupply'),
            flag: t('match.flag'),
          }}
        />
        <SectionTakeaway>{t('match.overview.fieldScatterTakeaway')}</SectionTakeaway>
        <table className="table table-sm mt-3">
          <thead>
            <tr>
              <th>{t('match.name')}</th>
              <th>{fieldWindow === '90d' ? t('match.overview.projects90d') : t('match.overview.projectsAll')}</th>
              <th>{t('match.overview.share')}</th>
              <th>{t('match.lawyers')}</th>
              <th>{t('match.overview.expertiseCoverage')}</th>
              <th>{t('match.overview.demandSupply')}</th>
              <th>{t('match.flag')}</th>
            </tr>
          </thead>
          <tbody>
            {fieldUsageRows.map((row) => (
              <tr key={row.field_id} className={row.flag ? `flag-${row.flag}` : ''}>
                <td>{row.name_et}</td>
                <td>{fieldWindow === '90d' ? row.count90d : row.countAll}</td>
                <td>{fmtPct(fieldWindow === '90d' ? row.share90d : row.shareAll)}</td>
                <td>{row.lawyerCount}</td>
                <td>{row.expertiseCount ?? 0}</td>
                <td>{row.demandSupplyRatio != null ? fmtNum(row.demandSupplyRatio, 2) : '—'}</td>
                <td>{row.flag || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(overview?.fieldUsage?.unmappedTags || []).length > 0 && (
          <>
            <h6 className="mt-3">{t('match.overview.unmappedTags')}</h6>
            <ul className="small">
              {overview.fieldUsage.unmappedTags.map((u) => (
                <li key={u.tag}>{u.tag}: {u.count90d} (90d) / {u.countAll} (all)</li>
              ))}
            </ul>
          </>
        )}
        <SectionTakeaway>
          {t('match.overview.fieldUsageTakeaway', {
            bottleneck: overview?.fieldUsage?.bottleneckCount ?? 0,
            unused: overview?.fieldUsage?.unusedCount ?? 0,
            unmapped: overview?.fieldUsage?.unmappedProjectCount ?? 0,
          })}
        </SectionTakeaway>
      </section>

      {jaccardSummary && (
        <section className="match-overview-section mb-4">
          <h4>{t('match.jaccardHigh')}</h4>
          <p className="small">
            {t('match.overview.jaccardSummary', {
              median: fmtNum(jaccardSummary.medianJaccard),
              over08: jaccardSummary.pairsOver08,
              total: jaccardSummary.totalPairs,
            })}
          </p>
          {highJaccardPairs.length > 0 && (
            <details>
              <summary>{t('match.overview.jaccardListToggle', { count: highJaccardPairs.length })}</summary>
              <ul className="mt-2">
                {highJaccardPairs.map((p, i) => (
                  <li key={i}>{p.nameA || p.fieldA} ↔ {p.nameB || p.fieldB}: {fmtNum(p.jaccard)}</li>
                ))}
              </ul>
            </details>
          )}
          <SectionTakeaway>
            {t('match.overview.jaccardTakeaway', {
              over08: jaccardSummary.pairsOver08,
              total: jaccardSummary.totalPairs,
            })}
          </SectionTakeaway>
        </section>
      )}

      {integrity && (
        <section className="mb-4 match-overview-section match-timestamp-panel">
          <h4>{t('match.overview.timestampTitle')}</h4>
          <p className="text-muted small">{t('match.overview.timestampExplain')}</p>
          <ul>
            <li>{t('match.overview.timestampBefore', {
              count: integrity.beforeCreatedAt,
              share: fmtPct(integrity.beforeCreatedAtShare),
            })}</li>
            <li>{t('match.overview.timestampLag', {
              count: integrity.lagOver30Days,
              share: fmtPct(integrity.lagOver30DaysShare),
            })}</li>
          </ul>
          {integrity.beforeCreatedAtSamples?.length > 0 && (
            <p className="small">{t('match.overview.timestampSamples')}: {integrity.beforeCreatedAtSamples.join(', ')}</p>
          )}
          <SectionTakeaway variant={integrity.beforeCreatedAt > 0 ? 'warning' : 'muted'}>
            {t('match.overview.timestampTakeaway', {
              count: integrity.beforeCreatedAt,
              share: fmtPct(integrity.beforeCreatedAtShare),
            })}
          </SectionTakeaway>
        </section>
      )}

      <section className="match-overview-section mb-4">
        <h4 className="mt-2">{t('match.crossTab')}</h4>
        <p className="text-muted">{t('match.appliedTypes')}: {(overview?.appliedTypes || []).join(', ')}</p>
        {overview?.typeCounts && (
          <details className="mb-2">
            <summary>{t('match.typeCounts')}</summary>
            <pre className="small">{JSON.stringify(overview.typeCounts, null, 2)}</pre>
          </details>
        )}
        {overview?.waitingCounts && (
          <p>{t('match.waitingCounts')}: raw={overview.waitingCounts.waitingRaw}, Uq={overview.waitingCounts.uq ?? overview.waitingCounts.waitingEffective}, before filter={overview.waitingCounts.totalBeforeFilter}</p>
        )}
        <table className="table table-sm table-striped">
          <thead>
            <tr><th>project_type</th><th>status</th><th>has-task</th><th>has-msg</th><th>age</th><th>count</th></tr>
          </thead>
          <tbody>
            {crossTabRows.slice(0, 100).map((r) => (
              <tr key={r.key}><td>{r.project_type}</td><td>{r.status}</td><td>{r.hasTask}</td><td>{r.hasMsg}</td><td>{r.age}</td><td>{r.count}</td></tr>
            ))}
          </tbody>
        </table>
        <SectionTakeaway>{t('match.overview.crossTabTakeaway')}</SectionTakeaway>
      </section>

      <ExcludeModal
        open={!!excludeTarget}
        project={excludeTarget}
        onClose={() => setExcludeTarget(null)}
        onSubmit={handleExclude}
        t={t}
      />
    </div>
  );
}
